import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import { Landmark, Globe2, ShieldAlert, Route } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

function Count({ to }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [v, setV] = useState(to);
  useEffect(() => {
    if (!inView) return undefined;
    const c = animate(0, to, { duration: 2, ease: [0.22, 1, 0.36, 1], onUpdate: (x) => setV(Math.round(x)) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{v}</span>;
}

const Card = ({ className = "", children, testid, delay = 0 }) => (
  <Reveal delay={delay} className={`group relative overflow-hidden border border-white/10 bg-[#0d1524] p-7 transition-colors duration-300 hover:border-sky-400/40 sm:p-9 ${className}`} data-testid={testid}>
    <div className="beam-border absolute inset-x-0 top-0 h-px opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    {children}
  </Reveal>
);

export function NetworkBento() {
  return (
    <section id="network" className="relative mx-auto max-w-[1400px] px-5 py-28 sm:px-10 sm:py-40" data-testid="network-section">
      <Reveal className="mb-16 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-5"><SectionLabel index="01">Banking network</SectionLabel></div>
        <h2 className="font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl lg:col-span-7">
          Our own banks at home. <span className="text-slate-400">A continental bench abroad.</span>
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        <Card className="md:col-span-7 md:min-h-[360px]" testid="bento-banks">
          <Landmark className="h-6 w-6 text-sky-400" />
          <p className="mt-10 font-display text-[7rem] font-semibold leading-none tracking-tighter sm:text-[10rem]" data-testid="stat-banks"><Count to={135} /></p>
          <p className="mt-4 max-w-md text-slate-300">Acquiring and settlement banks reachable through the MidHq + FinBPM network.</p>
        </Card>
        <Card className="md:col-span-5" testid="bento-countries" delay={0.08}>
          <Globe2 className="h-6 w-6 text-[var(--gold)]" />
          <p className="mt-10 font-display text-[7rem] font-semibold leading-none tracking-tighter text-[var(--gold)] sm:text-[10rem]" data-testid="stat-countries"><Count to={29} /></p>
          <p className="mt-4 text-slate-300">Countries where we can place and settle your volume.</p>
        </Card>
        <Card className="md:col-span-4" testid="bento-own-banks" delay={0.05}>
          <ShieldAlert className="h-6 w-6 text-sky-400" />
          <h3 className="mt-8 font-display text-2xl font-medium">High-risk first</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">Underwriting built around the verticals traditional processors turn away — with our own banks behind the decision.</p>
        </Card>
        <Card className="md:col-span-4" testid="bento-hubs" delay={0.1}>
          <Route className="h-6 w-6 text-[var(--gold)]" />
          <h3 className="mt-8 font-display text-2xl font-medium">Key corridors</h3>
          <ul className="mt-4 space-y-2 font-mono text-sm">
            {[["US", "MidHq desk"], ["MX", "Mexico"], ["BG", "Bulgaria"], ["NL", "Amsterdam"]].map(([c, n]) => (
              <li key={c} className="flex justify-between border-b border-white/10 pb-2"><span className="text-sky-300">{c}</span><span className="text-slate-300">{n}</span></li>
            ))}
          </ul>
        </Card>
        <Card className="md:col-span-4" testid="bento-tagteam" delay={0.15}>
          <p className="eyebrow">Tag-team model</p>
          <h3 className="mt-8 font-display text-2xl font-medium">One merchant. Two desks.</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">MidHq boards US-side processing; FinBPM handles international Merchant of Record, tax and risk. Referrals flow both ways.</p>
        </Card>
      </div>
    </section>
  );
}
